import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';
import { parseQuery } from '@/utils/query';

export const http = createDashHttpClient(env.schedulerApiUrl, {
  camelizeKeys: true,
  decamelizeIgnoreKeys: ['pinterestBoard'],
});

export const fetchPosts = (
  { platform, brandId, brandIds, start, end, sort, status, limit, hasScheduledTime },
  config = {},
) =>
  http.get(`/${platform}/scheduled_posts`, {
    ...config,
    params: {
      brandId,
      brandIds,
      start,
      end,
      sort,
      status: status?.join?.(','),
      limit,
      hasScheduledTime,
      mediaV2: true,
    },
  });

export const fetchPost = ({ platform, id }, config = {}) =>
  http.get(`/${platform}/scheduled_posts/${id}`, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const createPost = ({ platform, data }, config = {}) =>
  http.post(`/${platform}/scheduled_posts`, data, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const batchCreatePost = ({ platform, data }, config = {}) =>
  http.post(`/${platform}/scheduled_posts/batch`, data, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const updatePost = ({ platform, id, data }, config = {}) =>
  http.patch(`/${platform}/scheduled_posts/${id}`, data, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const deletePost = ({ platform, id }, config = {}) =>
  http.delete(`/${platform}/scheduled_posts/${id}`, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const reorderPost = ({ platform, id, brandId, after }, config = {}) =>
  http.post(
    `/${platform}/scheduled_posts/${id}/reorder`,
    { after, brandId },
    {
      ...config,
      params: {
        mediaV2: true,
      },
    },
  );

export const fetchEvents = ({ brandId }, config = {}) =>
  http.get('/events', {
    ...config,
    params: { brandId },
  });

export const createEvent = (
  { brandId, title, startDate, endDate, description, color },
  config = {},
) =>
  http.post(
    '/events',
    {
      brandId,
      title,
      startDate,
      endDate,
      description,
      color,
    },
    config,
  );

export const updateEvent = (
  { id, brandId, title, startDate, endDate, description, color },
  config = {},
) =>
  http.patch(
    `/events/${id}`,
    {
      brandId,
      title,
      startDate,
      endDate,
      description,
      color,
    },
    config,
  );

export const deleteEvent = ({ id }, config = {}) => http.delete(`/events/${id}`, config);

export const updateApprovalRequest = ({ approvalRequestId, approvalStatus }, config = {}) =>
  http.patch(
    `/approval_requests/${approvalRequestId}`,
    { approval_status: approvalStatus },
    config,
  );

export async function getRecommendedPublishTimes({ brandId, platform, timezone }, config = {}) {
  return http.get(`/recommended_publish_times`, {
    ...config,
    params: { brandId, platform_type: platform.toUpperCase(), timezone },
  });
}

/**
 * Validates an RSS feed URL with brand and name context
 * @param {Object} params - The validation parameters
 * @param {string} params.url - The RSS feed URL to validate
 * @param {number} params.brandId - The brand ID associated with the RSS feed
 * @param {string} params.name - The name/title of the RSS feed
 * @param {number} [params.rssFeedId] - The RSS feed ID to exclude from duplicate checks (for editing)
 * @param {Object} [config={}] - Optional axios configuration
 * @returns {Promise<Object>} The validation response
 */
export async function validateRssFeed({ url, brandId, name, rssFeedId }, config = {}) {
  const data = { url, brandId, name };
  if (rssFeedId) {
    data.rssFeedId = rssFeedId;
  }
  return http.post(`/rss_feeds/validate`, data, config);
}

/**
 * Retrieves a list of RSS feeds for specified brand IDs
 * @param {Array<number>} brandIds - Array of brand IDs to get RSS feeds for
 * @param {string} [url] - Optional URL to parse additional query parameters from
 * @param {number} limit - Number of RSS feeds to retrieve
 * @param {boolean} orderByBrandIds - Order results by the sequence of brandIds requested
 * @param {Object} [config] - Optional Axios configuration overrides
 * @returns {Promise<Object>} The RSS feeds list response
 */
export async function getRssFeedsList({ brandIds, url, limit, orderByBrandIds }, config = {}) {
  const params = {};
  if (url) {
    merge(params, parseQuery(url));
  }
  return http.get(`/rss_feeds`, {
    ...config,
    params: {
      ...params,
      brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds,
      limit,
      orderByBrandIds,
    },
  });
}

/**
 * Retrieves a single RSS feed by ID
 * @param {number} id - The ID of the RSS feed to retrieve
 * @param {Object} [config={}] - Optional axios configuration
 * @returns {Promise<Object>} The RSS feed response
 */
export async function getRssFeed(id, config = {}) {
  return http.get(`/rss_feeds/${id}`, config);
}

/**
 * Creates a new RSS feed with channel configurations
 * @param {Object} params - The RSS feed parameters
 * @param {number} params.brandId - The brand ID to associate the RSS feed with
 * @param {string} params.url - The URL of the RSS feed
 * @param {string} params.name - The name/title of the RSS feed
 * @param {string} params.frequency - The frequency setting (e.g., "SMART")
 * @param {string} params.status - The status of the RSS feed (e.g., "READY")
 * @param {boolean} params.enabled - Whether the RSS feed is enabled
 * @param {Object} params.channelConfigs - Channel-specific configurations for social media platforms
 * @param {Object} params.sampleItem - Sample item from the RSS feed for field mapping validation
 * @param {Object} [config={}] - Optional axios configuration
 * @returns {Promise<Object>} The created RSS feed response
 */
export async function createRssFeed(
  { brandId, url, name, frequency, status, enabled, channelConfigs, sampleItem },
  config = {},
) {
  const data = {
    brandId,
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  };

  return http.post(`/rss_feeds`, data, config);
}

/**
 * Updates an existing RSS feed with new configurations
 * @param {Object} params - The RSS feed update parameters
 * @param {number} params.id - The ID of the RSS feed to update
 * @param {number} params.brandId - The brand ID to associate the RSS feed with
 * @param {string} params.url - The URL of the RSS feed
 * @param {string} params.name - The name/title of the RSS feed
 * @param {string} params.frequency - The frequency setting (e.g., "SMART")
 * @param {string} params.status - The status of the RSS feed (e.g., "READY")
 * @param {boolean} params.enabled - Whether the RSS feed is enabled
 * @param {Object} params.channelConfigs - Channel-specific configurations for social media platforms
 * @param {Object} params.sampleItem - Sample item from the RSS feed for field mapping validation
 * @param {Object} [config={}] - Optional axios configuration
 * @returns {Promise<Object>} The updated RSS feed response
 */
export async function updateRssFeed(
  { id, brandId, url, name, frequency, status, enabled, channelConfigs, sampleItem },
  config = {},
) {
  const data = {
    brandId,
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  };

  return http.patch(`/rss_feeds/${id}`, data, config);
}

/**
 * Deletes an RSS feed by ID
 * @param {number} id - The ID of the RSS feed to delete
 * @param {Object} [config={}] - Optional axios configuration
 * @returns {Promise<Object>} The deletion response
 */
export async function deleteRssFeed(id, config = {}) {
  return http.delete(`/rss_feeds/${id}`, config);
}

/**
 * Search for LinkedIn organization followers for @mentions
 * @param {Object} params - The search parameters
 * @param {number} params.brandId - The brand ID to search followers for
 * @param {string} params.keyword - Search keyword (min 3 chars, max 50 chars)
 * @param {Object} [config={}] - Optional axios configuration
 * @returns {Promise<Object>} List of matching followers
 */
export async function searchLinkedInFollowers({ brandId, keyword }, config = {}) {
  return http.get(`/linkedin/followers/search`, {
    params: { brand_id: brandId, keyword },
    ...config,
  });
}

/**
 * Get LinkedIn member details (avatar, name, headline) by URNs
 * @param {Object} params - The request parameters
 * @param {number} params.brandId - The brand ID (used to fetch token)
 * @param {string} params.personUrns - Comma-separated list of LinkedIn URNs
 * @param {Object} [config={}] - Optional axios configuration
 * @returns {Promise<Object>} Member details including avatar_url, name, headline
 */
export async function getLinkedInMemberDetails({ brandId, personUrns }, config = {}) {
  return http.get(`/linkedin/member_details`, {
    params: { brand_id: brandId, person_urns: personUrns },
    paramsSerializer: {
      indexes: null, // serializes as person_urns=x&person_urns=y (no brackets)
    },
    ...config,
  });
}
